import { z } from 'zod';
import { cursorPageSchema, isoTimestampSchema, uuidSchema } from './common';

/*
 * Inventory (ADR-0019). Quantities are scaled integers at scale 1000 (`*_scaled`), in the
 * item's base unit unless a request names another `unit`. Money is integer minor units in
 * the branch currency.
 */

export const BASE_UNITS = ['g', 'ml', 'pcs'] as const;
export const baseUnitSchema = z.enum(BASE_UNITS);
export type BaseUnitCode = z.infer<typeof baseUnitSchema>;

export const LOCATION_KINDS = ['storage', 'kitchen', 'bar', 'other'] as const;
export const locationKindSchema = z.enum(LOCATION_KINDS);

export const STOCK_MOVEMENT_TYPES = [
  'opening',
  'receipt',
  'consumption',
  'waste',
  'adjustment',
  'count',
  'transfer_out',
  'transfer_in',
  'supplier_return',
  'revaluation',
] as const;
export const stockMovementTypeSchema = z.enum(STOCK_MOVEMENT_TYPES);
export type StockMovementType = z.infer<typeof stockMovementTypeSchema>;

const scaledSchema = z.number().int().refine(Number.isSafeInteger, 'Must be a safe integer');
const positiveScaledSchema = scaledSchema.refine((n) => n > 0, 'Must be positive');
const dateSchema = z.iso.date();

// ---- Items (tenant master data)

export const inventoryItemUnitSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  /** Base quantity (scaled ×1000) in one of this unit. */
  factor_scaled: z.number().int(),
  active: z.boolean(),
});
export type InventoryItemUnit = z.infer<typeof inventoryItemUnitSchema>;

export const inventoryItemSchema = z.object({
  id: uuidSchema,
  sku: z.string(),
  name: z.string(),
  category: z.string().nullable(),
  base_unit: baseUnitSchema,
  lot_tracked: z.boolean(),
  expiry_tracked: z.boolean(),
  shelf_life_days: z.number().int().nullable(),
  reorder_point_scaled: z.number().int().nullable(),
  active: z.boolean(),
  version: z.number().int(),
  units: z.array(inventoryItemUnitSchema),
});
export type InventoryItem = z.infer<typeof inventoryItemSchema>;

export const inventoryItemListResponseSchema = z.object({ items: z.array(inventoryItemSchema) });

const itemFields = {
  sku: z
    .string()
    .trim()
    .min(1)
    .max(32)
    .regex(/^[A-Za-z0-9._-]+$/, 'Letters, digits, dot, dash and underscore only'),
  name: z.string().trim().min(1).max(120),
  category: z.string().trim().min(1).max(60).nullable(),
  lot_tracked: z.boolean(),
  expiry_tracked: z.boolean(),
  shelf_life_days: z.number().int().min(1).max(3650).nullable(),
  reorder_point_scaled: scaledSchema.refine((n) => n >= 0, 'Must not be negative').nullable(),
};

const expiryNeedsLot = (v: { lot_tracked?: boolean; expiry_tracked?: boolean }) =>
  !(v.expiry_tracked === true && v.lot_tracked === false);
const expiryNeedsLotIssue = {
  message: 'Expiry tracking requires lot tracking.',
  path: ['expiry_tracked'],
};

export const createInventoryItemRequestSchema = z
  .object({
    ...itemFields,
    base_unit: baseUnitSchema,
    category: itemFields.category.default(null),
    lot_tracked: itemFields.lot_tracked.default(false),
    expiry_tracked: itemFields.expiry_tracked.default(false),
    shelf_life_days: itemFields.shelf_life_days.default(null),
    reorder_point_scaled: itemFields.reorder_point_scaled.default(null),
  })
  .refine(expiryNeedsLot, expiryNeedsLotIssue);

/** `base_unit` is immutable: the ledger stores base quantities. */
export const updateInventoryItemRequestSchema = z
  .object({
    expected_version: z.number().int().nonnegative(),
    ...z.object(itemFields).partial().shape,
    active: z.boolean().optional(),
  })
  .refine(expiryNeedsLot, expiryNeedsLotIssue);

const unitCodeSchema = z
  .string()
  .trim()
  .min(1)
  .max(12)
  .regex(/^[a-z0-9_-]+$/, 'Lower-case letters, digits, dash and underscore only');

export const createItemUnitRequestSchema = z.object({
  code: unitCodeSchema,
  name: z.string().trim().min(1).max(60),
  factor_scaled: positiveScaledSchema,
});
/** The factor is immutable; deactivate and create a new unit instead. */
export const updateItemUnitRequestSchema = z.object({
  name: z.string().trim().min(1).max(60).optional(),
  active: z.boolean().optional(),
});

// ---- Locations (branch)

export const stockLocationSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  kind: locationKindSchema,
  is_default: z.boolean(),
  active: z.boolean(),
  sort_order: z.number().int(),
  version: z.number().int(),
});
export type StockLocation = z.infer<typeof stockLocationSchema>;
export const stockLocationListResponseSchema = z.object({ items: z.array(stockLocationSchema) });

const locationFields = {
  name: z.string().trim().min(1).max(60),
  kind: locationKindSchema,
  is_default: z.boolean(),
  sort_order: z.number().int().min(0),
};
export const createStockLocationRequestSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1)
    .max(12)
    .regex(/^[A-Z0-9-]+$/, 'Upper-case letters, digits and dash only'),
  ...locationFields,
  kind: locationFields.kind.default('storage'),
  is_default: locationFields.is_default.default(false),
  sort_order: locationFields.sort_order.default(0),
});
export const updateStockLocationRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  ...z.object(locationFields).partial().shape,
  active: z.boolean().optional(),
});

// ---- Stock on hand

export const stockQuerySchema = z.object({
  location_id: uuidSchema.optional(),
  /** Include inactive items that still hold stock or value. Default: active only. */
  include_inactive: z.enum(['true', 'false']).optional(),
});

export const stockItemSchema = z.object({
  item_id: uuidSchema,
  sku: z.string(),
  name: z.string(),
  category: z.string().nullable(),
  base_unit: baseUnitSchema,
  lot_tracked: z.boolean(),
  active: z.boolean(),
  /** Branch total, or the location's quantity when filtered by location. */
  qty_scaled: z.number().int(),
  /**
   * Branch: the weighted-average pool value (exact). Location: qty × average (derived).
   */
  value_minor: z.number().int(),
  /** Average cost per display unit (kg / l / piece); null without stock. */
  avg_unit_cost_minor: z.number().int().nullable(),
  reorder_point_scaled: z.number().int().nullable(),
  below_reorder: z.boolean(),
  locations: z.array(z.object({ location_id: uuidSchema, qty_scaled: z.number().int() })),
});
export type StockItem = z.infer<typeof stockItemSchema>;

export const stockResponseSchema = z.object({
  currency: z.string(),
  as_of: isoTimestampSchema,
  valuation_method: z.literal('wac'),
  location_id: uuidSchema.nullable(),
  items: z.array(stockItemSchema),
  total_value_minor: z.number().int(),
});
export type StockResponse = z.infer<typeof stockResponseSchema>;

// ---- Ledger

export const stockMovementSchema = z.object({
  id: uuidSchema,
  posted_at: isoTimestampSchema,
  business_date: dateSchema,
  movement_type: stockMovementTypeSchema,
  item_id: uuidSchema,
  sku: z.string(),
  item_name: z.string(),
  base_unit: baseUnitSchema,
  location_id: uuidSchema,
  location_name: z.string(),
  lot_id: uuidSchema.nullable(),
  lot_code: z.string().nullable(),
  qty_delta_scaled: z.number().int(),
  value_delta_minor: z.number().int(),
  currency: z.string(),
  location_qty_after_scaled: z.number().int(),
  pool_qty_after_scaled: z.number().int(),
  pool_value_after_minor: z.number().int(),
  source_type: z.string(),
  source_id: z.string(),
  reason: z.string().nullable(),
  reversal_of_id: uuidSchema.nullable(),
  posted_by_name: z.string().nullable(),
});
export type StockMovement = z.infer<typeof stockMovementSchema>;

export const stockMovementQuerySchema = z.object({
  item_id: uuidSchema.optional(),
  location_id: uuidSchema.optional(),
  lot_id: uuidSchema.optional(),
  movement_type: stockMovementTypeSchema.optional(),
  source_id: z.string().max(64).optional(),
  from: dateSchema.optional(),
  to: dateSchema.optional(),
  cursor: z.string().max(512).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const stockMovementPageSchema = cursorPageSchema(stockMovementSchema);
export type StockMovementPage = z.infer<typeof stockMovementPageSchema>;

// ---- Valuation (P1.1)

export const valuationQuerySchema = z.object({
  /** Cutoff instant (UTC). Default: now. */
  as_of: isoTimestampSchema.optional(),
});

export const valuationRowSchema = z.object({
  item_id: uuidSchema,
  sku: z.string(),
  name: z.string(),
  category: z.string().nullable(),
  base_unit: baseUnitSchema,
  qty_scaled: z.number().int(),
  value_minor: z.number().int(),
  avg_unit_cost_minor: z.number().int().nullable(),
  movement_count: z.number().int(),
});
export type ValuationRow = z.infer<typeof valuationRowSchema>;

export const valuationResponseSchema = z.object({
  currency: z.string(),
  as_of: isoTimestampSchema,
  valuation_method: z.literal('wac'),
  definitions: z.record(z.string(), z.string()),
  rows: z.array(valuationRowSchema),
  by_category: z.array(
    z.object({ category: z.string().nullable(), value_minor: z.number().int() }),
  ),
  total_value_minor: z.number().int(),
  /** Ledger total vs the live cost pools; only for the current cutoff. */
  reconciliation: z
    .object({
      ledger_value_minor: z.number().int(),
      pool_value_minor: z.number().int(),
      difference_minor: z.number().int(),
      reconciled: z.boolean(),
    })
    .nullable(),
});
export type ValuationResponse = z.infer<typeof valuationResponseSchema>;

export const valuationHistoryQuerySchema = z.object({ from: dateSchema, to: dateSchema });
export const valuationHistoryResponseSchema = z.object({
  currency: z.string(),
  from: dateSchema,
  to: dateSchema,
  opening_value_minor: z.number().int(),
  days: z.array(
    z.object({
      business_date: dateSchema,
      inbound_value_minor: z.number().int(),
      outbound_value_minor: z.number().int(),
      closing_value_minor: z.number().int(),
    }),
  ),
});
export type ValuationHistory = z.infer<typeof valuationHistoryResponseSchema>;

// ---- Opening balances

export const openingBalanceLineSchema = z.object({
  item_id: uuidSchema,
  /** Quantity (scale 1000) in `unit`. */
  quantity_scaled: positiveScaledSchema,
  /** Standard unit (g, kg, ml, l, pcs) or the item's pack unit code. Default: base unit. */
  unit: z.string().trim().min(1).max(12).optional(),
  /** Cost per one `unit`, in minor units. */
  unit_cost_minor: z.number().int().min(0).refine(Number.isSafeInteger),
  lot_code: z.string().trim().min(1).max(40).optional(),
  expiry_date: dateSchema.optional(),
});

export const openingBalanceRequestSchema = z.object({
  location_id: uuidSchema,
  reason: z.string().trim().max(200).optional(),
  lines: z.array(openingBalanceLineSchema).min(1).max(100),
});

export const postingResultSchema = z.object({
  source_type: z.string(),
  source_id: z.string(),
  movements: z.array(stockMovementSchema),
});
export type PostingResult = z.infer<typeof postingResultSchema>;

// ---- Lots, holds, expiry (P1.2)

export const LOT_STATUSES = ['available', 'on_hold', 'quarantined', 'recalled'] as const;
export const lotStatusSchema = z.enum(LOT_STATUSES);
export type LotStatus = z.infer<typeof lotStatusSchema>;

export const lotSchema = z.object({
  id: uuidSchema,
  lot_code: z.string(),
  item_id: uuidSchema,
  sku: z.string(),
  item_name: z.string(),
  base_unit: baseUnitSchema,
  status: lotStatusSchema,
  expiry_date: dateSchema.nullable(),
  manufactured_date: dateSchema.nullable(),
  /** Relative to the branch business date; negative once expired; null without expiry. */
  days_to_expiry: z.number().int().nullable(),
  expired: z.boolean(),
  qty_scaled: z.number().int(),
  /** qty × branch average cost (derived, ADR-0019). */
  value_minor: z.number().int(),
  locations: z.array(
    z.object({ location_id: uuidSchema, location_name: z.string(), qty_scaled: z.number().int() }),
  ),
  source_type: z.string(),
  source_id: z.string(),
  /** Receiving document number and supplier, when the lot came from a goods receipt. */
  source_number: z.string().nullable(),
  supplier_name: z.string().nullable(),
  received_at: isoTimestampSchema,
  version: z.number().int(),
});
export type Lot = z.infer<typeof lotSchema>;

export const lotListQuerySchema = z.object({
  item_id: uuidSchema.optional(),
  location_id: uuidSchema.optional(),
  status: lotStatusSchema.optional(),
  /** Lot code (prefix match, case-insensitive). */
  q: z.string().trim().max(40).optional(),
  /** Include lots with no stock left. Default false. */
  include_empty: z.enum(['true', 'false']).optional(),
});
export const lotListResponseSchema = z.object({
  currency: z.string(),
  business_date: dateSchema,
  items: z.array(lotSchema),
});

export const lotHoldSchema = z.object({
  id: uuidSchema,
  reason: z.string(),
  source_type: z.enum(['manual', 'goods_receipt', 'recall']),
  held_by_name: z.string().nullable(),
  held_at: isoTimestampSchema,
  released_by_name: z.string().nullable(),
  released_at: isoTimestampSchema.nullable(),
  release_reason: z.string().nullable(),
});
export type LotHold = z.infer<typeof lotHoldSchema>;

export const lotDetailSchema = z.object({
  currency: z.string(),
  business_date: dateSchema,
  lot: lotSchema,
  holds: z.array(lotHoldSchema),
  /** Every ledger movement of the lot, oldest first (traceability). */
  movements: z.array(stockMovementSchema),
});
export type LotDetail = z.infer<typeof lotDetailSchema>;

export const lotHoldRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  reason: z.string().trim().min(3).max(300),
});

export const expiryAlertQuerySchema = z.object({
  horizon_days: z.coerce.number().int().min(0).max(90).default(7),
});
export const expiryAlertResponseSchema = z.object({
  currency: z.string(),
  business_date: dateSchema,
  horizon_days: z.number().int(),
  expired: z.array(lotSchema),
  expiring: z.array(lotSchema),
  exposure: z.object({
    expired_value_minor: z.number().int(),
    expiring_value_minor: z.number().int(),
  }),
});
export type ExpiryAlerts = z.infer<typeof expiryAlertResponseSchema>;

export const fefoQuerySchema = z.object({
  item_id: uuidSchema,
  location_id: uuidSchema,
  qty_scaled: z.coerce.number().int().positive().optional(),
});
export const fefoResponseSchema = z.object({
  business_date: dateSchema,
  /** Eligible lots in FEFO order. */
  eligible: z.array(lotSchema),
  /** Lots with stock that cannot be used without an override, and why. */
  blocked: z.array(lotSchema.extend({ block: z.enum(['expired', 'not_available']) })),
  /** Suggested allocation for `qty_scaled`; null if not requested or not enough stock. */
  allocation: z
    .array(z.object({ lot_id: uuidSchema, lot_code: z.string(), qty_scaled: z.number().int() }))
    .nullable(),
  shortfall_scaled: z.number().int(),
});
export type FefoResponse = z.infer<typeof fefoResponseSchema>;
